import type { Editor } from '@tiptap/react';
import { forwardRef, useMemo, useRef, useState } from 'react';
// --- Icons ---
import { BanIcon } from '@/components/tiptap-icons/ban-icon';
import { HighlighterIcon } from '@/components/tiptap-icons/highlighter-icon';
// --- Tiptap UI ---
import type {
	HighlightColor,
	UseColorHighlightConfig,
} from '@/components/tiptap-ui/color-highlight-button';
import {
	ColorHighlightButton,
	pickHighlightColorsByValue,
	useColorHighlight,
} from '@/components/tiptap-ui/color-highlight-button';
// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-ui-primitive/button';
import { ButtonGroup } from '@/components/tiptap-ui-primitive/button-group';
import { Card, CardBody, CardItemGroup } from '@/components/tiptap-ui-primitive/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/tiptap-ui-primitive/popover';
import { Separator } from '@/components/tiptap-ui-primitive/separator';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
// --- Hooks ---
import { useMenuNavigation } from '@/hooks/use-menu-navigation';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

export interface ColorHighlightPopoverContentProps {
	/**
	 * The Tiptap editor instance.
	 */
	editor?: Editor | null;
	/**
	 * Optional colors to use in the highlight popover.
	 * If not provided, defaults to a predefined set of colors.
	 */
	colors?: HighlightColor[];
	/**
	 * When true, uses the actual color value (colorValue) instead of CSS variable (value).
	 * @default false
	 */
	useColorValue?: boolean;
}

export interface ColorHighlightPopoverProps
	extends Omit<ButtonProps, 'type'>,
		Pick<UseColorHighlightConfig, 'editor' | 'hideWhenUnavailable' | 'onApplied'> {
	/**
	 * Optional colors to use in the highlight popover.
	 * If not provided, defaults to a predefined set of colors.
	 */
	colors?: HighlightColor[];
	/**
	 * When true, uses the actual color value (colorValue) instead of CSS variable (value).
	 * @default false
	 */
	useColorValue?: boolean;
}

export const ColorHighlightPopoverButton = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, children, ...props }, ref) => (
		<Button
			type="button"
			className={className}
			variant="ghost"
			data-appearance="default"
			role="button"
			tabIndex={-1}
			aria-label="Highlight text"
			tooltip="Highlight"
			ref={ref}
			{...props}
		>
			{children ?? <HighlighterIcon className="tiptap-button-icon" />}
		</Button>
	)
);

ColorHighlightPopoverButton.displayName = 'ColorHighlightPopoverButton';

export function ColorHighlightPopoverContent({
	editor,
	colors = pickHighlightColorsByValue([
		'var(--tt-color-highlight-green)',
		'var(--tt-color-highlight-blue)',
		'var(--tt-color-highlight-red)',
		'var(--tt-color-highlight-purple)',
		'var(--tt-color-highlight-yellow)',
	]),
	useColorValue = false,
}: ColorHighlightPopoverContentProps) {
	const { handleRemoveHighlight } = useColorHighlight({ editor });
	const isMobile = useIsBreakpoint();
	const containerRef = useRef<HTMLDivElement>(null);

	const menuItems = useMemo(
		() => [...colors, { label: 'Remove highlight', value: 'none' }],
		[colors]
	);

	const { selectedIndex } = useMenuNavigation({
		containerRef,
		items: menuItems,
		orientation: 'both',
		onSelect: (item) => {
			if (!containerRef.current) return false;
			const highlightedElement = containerRef.current.querySelector(
				'[data-highlighted="true"]'
			) as HTMLElement;
			if (highlightedElement) highlightedElement.click();
			if (item.value === 'none') handleRemoveHighlight();
			return true;
		},
		autoSelectFirstItem: false,
	});

	return (
		<Card ref={containerRef} tabIndex={0} style={isMobile ? { boxShadow: 'none', border: 0 } : {}}>
			<CardBody style={isMobile ? { padding: 0 } : {}}>
				<CardItemGroup orientation="horizontal">
					<ButtonGroup>
						{colors.map((color, index) => (
							<ButtonGroup key={color.value}>
								<ColorHighlightButton
									editor={editor}
									highlightColor={useColorValue ? color.colorValue : color.value}
									tooltip={color.label}
									aria-label={`${color.label} highlight color`}
									tabIndex={index === selectedIndex ? 0 : -1}
									data-highlighted={selectedIndex === index}
									useColorValue={useColorValue}
								/>
							</ButtonGroup>
						))}
					</ButtonGroup>
					<Separator />
					<ButtonGroup>
						<Button
							onClick={handleRemoveHighlight}
							aria-label="Remove highlight"
							tooltip="Remove highlight"
							tabIndex={selectedIndex === colors.length ? 0 : -1}
							type="button"
							role="menuitem"
							variant="ghost"
							data-highlighted={selectedIndex === colors.length}
						>
							<BanIcon className="tiptap-button-icon" />
						</Button>
					</ButtonGroup>
				</CardItemGroup>
			</CardBody>
		</Card>
	);
}

export function ColorHighlightPopover({
	editor: providedEditor,
	colors = pickHighlightColorsByValue([
		'var(--tt-color-highlight-green)',
		'var(--tt-color-highlight-blue)',
		'var(--tt-color-highlight-red)',
		'var(--tt-color-highlight-purple)',
		'var(--tt-color-highlight-yellow)',
	]),
	hideWhenUnavailable = false,
	useColorValue = false,
	onApplied,
	...props
}: ColorHighlightPopoverProps) {
	const { editor } = useTiptapEditor(providedEditor);
	const [isOpen, setIsOpen] = useState(false);
	const { isVisible, canColorHighlight, isActive, label, Icon } = useColorHighlight({
		editor,
		hideWhenUnavailable,
		onApplied,
	});

	if (!isVisible) return null;

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<ColorHighlightPopoverButton
					disabled={!canColorHighlight}
					data-active-state={isActive ? 'on' : 'off'}
					data-disabled={!canColorHighlight}
					aria-pressed={isActive}
					aria-label={label}
					tooltip={label}
					{...props}
				>
					<Icon className="tiptap-button-icon" />
				</ColorHighlightPopoverButton>
			</PopoverTrigger>
			<PopoverContent aria-label="Highlight colors">
				<ColorHighlightPopoverContent
					editor={editor}
					colors={colors}
					useColorValue={useColorValue}
				/>
			</PopoverContent>
		</Popover>
	);
}

export default ColorHighlightPopover;
